import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { Editor, Transforms } from "slate";
import { ReactEditor, useFocused, useSelected, useSlateStatic } from "slate-react";
import { useColor } from "@m/alchemy-theme";
import { Popper } from "../../../../popper/Popper";
import { PopperBox } from "../../../../popper/PopperBox";
import { focusEditor } from "../../utilities/index";
import { ImageToolbar } from "./ImageToolbar";
import { getStyleFromImageState, updateImage } from "./Image.utils";
export const ImageElement = (props) => {
    const { attributes, children, element } = props;
    const { children: elementChildren, type, ...imageProperties } = element;
    const editor = useSlateStatic();
    const [isToolbarOpen, setIsToolbarOpen] = useState(false);
    const selected = useSelected();
    const focused = useFocused();
    const imageRef = useRef(null);
    const stateRef = useRef({ selected, focused, isToolbarOpen });
    stateRef.current = { selected, focused, isToolbarOpen };
    useEffect(() => {
        if (element.href) {
            // Correct deprecated API. Should be safe enough to remove in 2024.
            Editor.normalize(editor, { force: true });
        }
    }, [element.href, editor]);
    /* Open via keyboard "enter" press when image is active in editor */
    useEffect(() => {
        const openOnEnter = (e) => {
            const { selected, focused, isToolbarOpen } = stateRef.current;
            if (e.key === "Enter" && selected && focused && !isToolbarOpen) {
                setIsToolbarOpen(true);
                e.preventDefault();
            }
        };
        window.addEventListener("keydown", openOnEnter, true);
        return () => {
            window.removeEventListener("keydown", openOnEnter, true);
        };
    }, [stateRef]);
    const imageStyles = getStyleFromImageState(imageProperties);
    const path = ReactEditor.findPath(editor, element);
    const focusBlue = useColor("base", { palette: "expressiveInformative" });
    const isHighlighted = (selected && focused) || (selected && isToolbarOpen);
    const highlightStyles = isHighlighted ? { outline: `2px solid ${focusBlue}` } : {};
    return (_jsxs("span", { ...attributes, children: [_jsx("img", { contentEditable: false, style: {
                    ...imageStyles,
                    ...highlightStyles
                }, onLoad: (e) => {
                    updateImage(editor, element, {
                        width: imageProperties.width || e.currentTarget.naturalWidth,
                        height: imageProperties.height || e.currentTarget.naturalHeight,
                        naturalHeight: e.currentTarget.naturalHeight,
                        naturalWidth: e.currentTarget.naturalWidth
                    });
                }, ref: imageRef, src: imageProperties.src, alt: imageProperties.alt || "", width: imageProperties.width }), _jsx(Popper, { triggerRef: imageRef, isOpen: isToolbarOpen, onOpenCallback: (e) => {
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    e.preventDefault();
                    Transforms.select(editor, path);
                    setIsToolbarOpen(true);
                }, onCloseCallback: (e) => {
                    Transforms.select(editor, path);
                    focusEditor(editor);
                    e.stopPropagation();
                    setIsToolbarOpen(false);
                }, shouldCloseOnScroll: true, shouldNeverThrowFocus: true, overlay: _jsx(PopperBox, { maxWidth: "none", hasPadding: false, borderRadius: "22px", children: _jsx(ImageToolbar, { element: element, imageProperties: imageProperties, path: path, onVerticalAlign: (verticalAlign) => updateImage(editor, element, { verticalAlign }), horizontalAlign: imageProperties.horizontalAlign, onHorizontalAlign: (horizontalAlign) => updateImage(editor, element, { horizontalAlign }), verticalAlign: imageProperties.verticalAlign }) }) }), children] }));
};
